import { Component, signal, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, Validators } from '@angular/forms';
import { Router, RouterLink, ActivatedRoute } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { ContactService } from '../../../core/services/contact.service';
import { Contact } from '../../../core/models/models';
import { ConfirmDialogComponent } from '../../../shared/components/confirm-dialog.component';
import { FormValidators } from '../../../core/validators/form-validators';

@Component({
  selector: 'app-contact-edit',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, RouterLink, MatIconModule, ConfirmDialogComponent],
  templateUrl: `contact-edit.component.html`,
  styleUrls: [`contact-edit.component.css`]
})
export class ContactEditComponent implements OnInit {
  private fb = inject(FormBuilder);
  private contactService = inject(ContactService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);

  isLoading = signal(false);
  loadingContact = signal(true);
  errorMessage = signal<string | null>(null);
  contactId: number | null = null;
  canDelete = signal(false);
  showDeleteModal = signal(false);

  contactForm = this.fb.group({
    nom: ['', [Validators.required]],
    prenom: ['', [Validators.required]],
    telephone: ['', [FormValidators.telephoneFr()]],
    email: ['', [Validators.email]],
    archived: [false]
  });

  ngOnInit() {
    const idParam = this.route.snapshot.paramMap.get('id');
    if (idParam) {
      this.contactId = +idParam;
      this.loadContact(this.contactId);
      this.checkCanDelete(this.contactId);
    } else {
      this.router.navigate(['/contacts']);
    }
  }

  loadContact(id: number) {
    this.contactService.getContactById(id).subscribe({
      next: (contact) => {
        this.contactForm.patchValue({
          nom: contact.nom,
          prenom: contact.prenom,
          telephone: contact.telephone,
          email: contact.email,
          archived: contact.archived
        });
        this.loadingContact.set(false);
      },
      error: (err) => {
        this.errorMessage.set('Erreur lors du chargement du contact.');
        this.loadingContact.set(false);
      }
    });
  }

  checkCanDelete(id: number) {
    this.contactService.canDelete(id).subscribe(can => this.canDelete.set(can));
  }

  onArchive() {
    if (this.contactId) {
      this.isLoading.set(true);
      const contact = { ...this.contactForm.value, archived: true } as Contact;
      this.contactService.updateContact(this.contactId, contact).subscribe({
        next: () => this.router.navigate(['/contacts']),
        error: (err) => {
          this.errorMessage.set('Erreur lors de l\'archivage.');
          this.isLoading.set(false);
        }
      });
    }
  }

  onDelete() {
    this.showDeleteModal.set(true);
  }

  confirmDelete() {
    if (this.contactId) {
      this.contactService.deleteContact(this.contactId).subscribe({
        next: () => this.router.navigate(['/contacts']),
        error: () => {
          this.errorMessage.set('Erreur lors de la suppression.');
          this.showDeleteModal.set(false);
        }
      });
    }
  }

  cancelDelete() {
    this.showDeleteModal.set(false);
  }

  onSubmit() {
    if (this.contactForm.valid && this.contactId) {
      this.isLoading.set(true);
      this.errorMessage.set(null);

      this.contactService.updateContact(this.contactId, this.contactForm.value as any).subscribe({
        next: () => {
          this.router.navigate(['/contacts']);
        },
        error: (err) => {
          this.errorMessage.set(err.error?.message || err.error || 'Une erreur est survenue lors de la mise à jour du contact.');
          this.isLoading.set(false);
        }
      });
    }
  }
}
